import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { UserContext } from '../UserContext';
import axios from '../axiosInstance';
import { formatDate } from '../utils/dateFormat';
import {
  FaFileAlt, FaFolderOpen, FaCheck, FaPaperPlane, FaUndo, FaDownload, FaInfoCircle
} from 'react-icons/fa';
import '../Pages.css';
import '../Pips/Pips.css';   // shared design system
import '../TM/TM.css';       // .tx-table is reused
import './STR.css';

const STATUS_LABEL = {
  draft:        'Draft',
  reviewed:     'Reviewed',
  submitted:    'Submitted',
  acknowledged: 'Acknowledged',
  withdrawn:    'Withdrawn'
};

const fmt = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy}, ${hh}:${mi}`;
};

const money = (amount, currency) => {
  if (amount == null) return '—';
  return `${currency || 'NAD'} ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const hasPermission = (user, name) =>
  !!user && (
    user.is_system_admin === true ||
    (Array.isArray(user.permissions) && user.permissions.includes(name))
  );

const customerName = (r) =>
  r.customer_name_individual || r.customer_name_business || `Customer #${r.customer_id}`;

function exportToPdf(report) {
  const doc = new jsPDF({ unit: 'pt' });
  const content = report.content || {};
  const margin = 40;
  let y = margin;

  doc.setFontSize(16);
  doc.text(`Suspicious Transaction Report — ${report.reference}`, margin, y);
  y += 22;

  doc.setFontSize(10);
  doc.text(`Status: ${STATUS_LABEL[report.status] || report.status}`, margin, y); y += 14;
  doc.text(`Generated: ${fmt(report.generated_at)}`, margin, y); y += 14;
  if (report.submitted_to)       { doc.text(`Submitted to: ${report.submitted_to}`, margin, y); y += 14; }
  if (report.external_reference) { doc.text(`Regulator ref: ${report.external_reference}`, margin, y); y += 14; }
  y += 8;

  doc.setFontSize(12);
  doc.text('Reason for report', margin, y); y += 16;
  doc.setFontSize(10);
  const reasonLines = doc.splitTextToSize(report.reason_summary || '', 515);
  doc.text(reasonLines, margin, y); y += reasonLines.length * 12 + 8;

  if (report.narrative) {
    doc.setFontSize(12);
    doc.text('AML officer narrative', margin, y); y += 16;
    doc.setFontSize(10);
    const narrLines = doc.splitTextToSize(report.narrative, 515);
    doc.text(narrLines, margin, y); y += narrLines.length * 12 + 8;
  }

  if (content.subject) {
    doc.setFontSize(12);
    doc.text('Subject', margin, y); y += 16;
    doc.setFontSize(10);
    const subj = content.subject;
    const lines = subj.kind === 'individual'
      ? [
          `Name: ${subj.full_name || '—'}`,
          `DOB: ${formatDate(subj.date_of_birth)}    Nationality: ${subj.nationality || '—'}`,
          `ID: ${subj.id_type || ''} ${subj.id_number || ''}   Country: ${subj.id_country_of_issue || '—'}`,
          `Occupation: ${subj.occupation || '—'}   Employer: ${subj.employer || '—'}`,
          `Source of funds: ${subj.source_of_funds || '—'}`,
          `Flags: ${[subj.is_pep && 'PEP', subj.is_pip && 'PIP', subj.is_sanctioned && 'Sanctioned'].filter(Boolean).join(', ') || 'none'}`
        ]
      : [
          `Legal name: ${subj.legal_name || '—'}    Trading: ${subj.trading_name || '—'}`,
          `Reg #: ${subj.registration_number || '—'}    Tax #: ${subj.tax_number || '—'}`,
          `Type: ${subj.business_type || '—'}    Sector: ${subj.industry_sector || '—'}`,
          `Country: ${subj.country_of_incorporation || '—'}   Incorporated: ${formatDate(subj.date_of_incorporation)}`,
          `Directors: ${(subj.directors || []).length}    UBOs: ${(subj.ubos || []).length}`
        ];
    doc.text(lines, margin, y); y += lines.length * 12 + 10;
  }

  if (content.transactions && Array.isArray(content.transactions.rows) && content.transactions.rows.length) {
    autoTable(doc, {
      startY: y,
      head: [['Date', 'Direction', 'Amount', 'Channel', 'Counterparty', 'Country', 'Ref']],
      body: content.transactions.rows.map(t => [
        fmt(t.occurred_at),
        t.direction,
        money(t.amount, t.currency),
        t.channel || '—',
        t.counterparty_name || '—',
        t.counterparty_country || '—',
        t.external_ref || '—'
      ]),
      styles: { fontSize: 8 }
    });
    y = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(10);
    doc.text(`Total: ${money(content.transactions.total_amount)}    (${content.transactions.count} transactions)`, margin, y);
    y += 14;
  }

  doc.save(`${report.reference}.pdf`);
}

function ManageSTRReports() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const routeLocation = useLocation();

  const canRead    = hasPermission(user, 'str_reports.read');
  const canUpdate  = hasPermission(user, 'str_reports.update');
  const canReview  = hasPermission(user, 'str_reports.review');
  const canSubmit  = hasPermission(user, 'str_reports.submit');
  const canWithdraw = hasPermission(user, 'str_reports.withdraw');
  const canExport  = hasPermission(user, 'str_reports.export');

  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({ status: 'all', report_type: 'all', query: '' });
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [actionModal, setActionModal] = useState(null);
  const [actionVals, setActionVals] = useState({});
  const [actionBusy, setActionBusy] = useState(false);

  const fetchStats = useCallback(async () => {
    try { const r = await axios.get('/str-reports/stats'); setStats(r.data); }
    catch (e) { console.error(e.message); }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = new URLSearchParams();
      if (filter.status !== 'all')      p.set('status', filter.status);
      if (filter.report_type !== 'all') p.set('report_type', filter.report_type);
      if (filter.query.trim())          p.set('query', filter.query.trim());
      p.set('limit', '500');
      const r = await axios.get(`/str-reports?${p.toString()}`);
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError(e.message || 'Failed to load STRs');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (!canRead) return;
    fetchStats();
    fetchList();
  }, [canRead, fetchStats, fetchList]);

  const openDetail = useCallback(async (id) => {
    setSelectedId(id);
    setDetail(null);
    try {
      const r = await axios.get(`/str-reports/${id}`);
      setDetail(r.data);
    } catch (e) {
      setError(e.message || 'Failed to load STR');
      setSelectedId(null);
    }
  }, []);

  useEffect(() => {
    if (!canRead) return;
    const qs = new URLSearchParams(routeLocation.search);
    const idParam = qs.get('id');
    if (idParam && /^\d+$/.test(idParam)) openDetail(Number(idParam));
  }, [canRead, routeLocation.search, openDetail]);

  const openModal = (cfg) => { setActionVals(cfg.initial || {}); setActionModal(cfg); };

  const runAction = async () => {
    if (!actionModal?.onConfirm) return;
    try {
      setActionBusy(true);
      await actionModal.onConfirm(actionVals);
      setActionModal(null);
      setActionVals({});
      if (selectedId) {
        const r = await axios.get(`/str-reports/${selectedId}`);
        setDetail(r.data);
      }
      fetchList();
      fetchStats();
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Action failed');
    } finally {
      setActionBusy(false);
    }
  };

  const saveEdits = () => openModal({
    title: 'Edit STR draft',
    initial: {
      reason_summary: detail.reason_summary || '',
      narrative: detail.narrative || ''
    },
    fields: [
      { key: 'reason_summary', label: 'Reason (1-line headline)', type: 'text' },
      { key: 'narrative',      label: 'Officer narrative',         type: 'textarea' }
    ],
    onConfirm: async (v) => {
      await axios.patch(`/str-reports/${selectedId}`, {
        reason_summary: v.reason_summary,
        narrative: v.narrative
      });
    }
  });

  const markReviewed = () => openModal({
    title: 'Sign off this STR',
    fields: [],
    onConfirm: async () => { await axios.post(`/str-reports/${selectedId}/review`); }
  });

  const submitToRegulator = () => openModal({
    title: 'Submit STR to regulator',
    initial: { submitted_to: 'FIC Namibia' },
    fields: [
      { key: 'submitted_to',       label: 'Submitted to', type: 'text' },
      { key: 'external_reference', label: 'Regulator confirmation # (optional)', type: 'text' }
    ],
    onConfirm: async (v) => {
      if (!(v.submitted_to || '').trim()) throw new Error('Recipient is required');
      await axios.post(`/str-reports/${selectedId}/submit`, {
        submitted_to: v.submitted_to.trim(),
        external_reference: (v.external_reference || '').trim() || undefined
      });
    }
  });

  const acknowledge = () => openModal({
    title: 'Mark as acknowledged by regulator',
    fields: [],
    onConfirm: async () => { await axios.post(`/str-reports/${selectedId}/acknowledge`); }
  });

  const withdraw = () => openModal({
    title: 'Withdraw STR',
    fields: [{ key: 'reason', label: 'Reason for withdrawal (required)', type: 'textarea' }],
    onConfirm: async (v) => {
      if (!(v.reason || '').trim()) throw new Error('Reason is required');
      await axios.post(`/str-reports/${selectedId}/withdraw`, { reason: v.reason.trim() });
    }
  });

  if (user && !canRead) {
    return (
      <div className="page-container">
        <div className="alert-banner alert-banner--warning" style={{ marginTop: '2rem' }}>
          <span className="alert-banner__body">
            <FaInfoCircle /> You don't have permission to view STR Reports.
            Ask your administrator for the <code>str_reports.read</code> permission.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Stat cards */}
      {user && (
        <div className="stats-section">
          {/* Shared stat-card layout (Pips.css): compact cards */}
          <div className="stats-layout__rest">
          <div className="stats-grid">
            <div className="stat-card stat-card--teal">
              <div className="stat-label">Total reports</div>
              <div className="stat-value stat-value--teal">{stats ? stats.total : '—'}</div>
            </div>
            <div className="stat-card stat-card--orange">
              <div className="stat-label">Pending review</div>
              <div className="stat-value stat-value--orange">{stats ? (stats.by_status?.draft || 0) : '—'}</div>
            </div>
            <div className="stat-card stat-card--blue">
              <div className="stat-label">Awaiting submission</div>
              <div className="stat-value stat-value--blue">{stats ? (stats.by_status?.reviewed || 0) : '—'}</div>
            </div>
            <div className="stat-card stat-card--purple">
              <div className="stat-label">Submitted</div>
              <div className="stat-value stat-value--purple">
                {stats ? ((stats.by_status?.submitted || 0) + (stats.by_status?.acknowledged || 0)) : '—'}
              </div>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Filters */}
      <section className="form-section" style={{ marginBottom: '1rem' }}>
        <header className="form-section__head">
          <h4 className="form-section__title">STR queue</h4>
          <p className="form-section__subtitle">Regulatory reports — STR (suspicion-driven) and CTR (cash-threshold).</p>
        </header>
        <div className="form-grid-3">
          <div className="form-group">
            <label>Report type</label>
            <select value={filter.report_type} onChange={e => setFilter(f => ({ ...f, report_type: e.target.value }))}>
              <option value="all">All types</option>
              <option value="STR">STR (suspicious)</option>
              <option value="CTR">CTR (cash threshold)</option>
              <option value="SAR">SAR</option>
            </select>
          </div>
          <div className="form-group">
            <label>Status</label>
            <select value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}>
              <option value="all">All statuses</option>
              {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Search</label>
            <input type="text" placeholder="reference / reason"
                   value={filter.query}
                   onChange={e => setFilter(f => ({ ...f, query: e.target.value }))}
                   onKeyDown={e => { if (e.key === 'Enter') fetchList(); }} />
          </div>
          <div className="form-group">
            <label>&nbsp;</label>
            <button className="export-button" onClick={fetchList} disabled={loading}>
              {loading ? 'Loading…' : 'Apply filters'}
            </button>
          </div>
        </div>
      </section>

      {error && (
        <div className="alert-banner alert-banner--danger" style={{ marginBottom: '1rem' }}>
          <span className="alert-banner__body"><FaInfoCircle /> {error}</span>
        </div>
      )}

      {/* STR list */}
      <div className="str-list">
        {rows.length === 0 && !loading && (
          <p style={{ color: 'var(--text-secondary,#607d8b)' }}>No reports match the current filters.</p>
        )}
        {rows.map(r => (
          <div key={r.id} className={`str-card status-${r.status}`} onClick={() => openDetail(r.id)}>
            <div className="str-card__top">
              <div style={{ flex: 1 }}>
                <div className="str-card__ref">
                  {r.reference}
                  <span style={{ marginLeft: '0.5rem', padding: '0.1rem 0.4rem', background: 'var(--bg-tertiary,#eceff1)',
                                 color: 'var(--text-secondary,#607d8b)', fontSize: '0.65rem', letterSpacing: '0.04em' }}>
                    {r.report_type || 'STR'}
                  </span>
                </div>
                <div className="str-card__title">{r.reason_summary}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                  {customerName(r)}
                  {r.case_number && <> &middot; <FaFolderOpen /> {r.case_number}</>}
                </div>
              </div>
              <span className={`str-pill str-pill--${r.status}`}>{STATUS_LABEL[r.status]}</span>
            </div>
            <div className="str-card__meta">
              <span>Generated {fmt(r.generated_at)}</span>
              {r.reviewed_at && <span>Reviewed {fmt(r.reviewed_at)}</span>}
              {r.submitted_at && <span>Submitted {fmt(r.submitted_at)} → {r.submitted_to}</span>}
              {r.external_reference && <span>Ref {r.external_reference}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Detail modal */}
      {selectedId && (
        <div className="modal-overlay" onClick={() => setSelectedId(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 820 }}>
            <button className="modal-close" onClick={() => setSelectedId(null)}>×</button>
            {!detail ? <div>Loading…</div> : (
              <>
                <h2 style={{ marginTop: 0 }}>
                  <FaFileAlt style={{ marginRight: '0.4rem' }} />
                  {detail.reference}
                </h2>
                <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <span className={`str-pill str-pill--${detail.status}`}>{STATUS_LABEL[detail.status]}</span>
                  {detail.case_number && (
                    <button className="btn-cancel" style={{ padding: '0.2rem 0.55rem', fontSize: '0.7rem' }}
                            onClick={() => navigate(`/cases?id=${detail.case_id}`)}>
                      <FaFolderOpen /> {detail.case_number}
                    </button>
                  )}
                  {canExport && (
                    <>
                      <button className="export-button" style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem', marginLeft: 'auto' }}
                              onClick={() => exportToPdf(detail)}>
                        <FaDownload /> Export PDF
                      </button>
                      <button className="export-button" style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem' }}
                              onClick={async () => {
                                try {
                                  const r = await axios.get(`/str-reports/${detail.id}/goaml-xml`, { responseType: 'blob' });
                                  const blob = new Blob([r.data], { type: 'application/xml' });
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `${detail.reference}.goaml.xml`;
                                  document.body.appendChild(a);
                                  a.click();
                                  document.body.removeChild(a);
                                  window.URL.revokeObjectURL(url);
                                } catch (err) {
                                  setError(err.response?.data?.error?.message || err.message || 'goAML XML export failed');
                                }
                              }}>
                        <FaDownload /> goAML XML
                      </button>
                    </>
                  )}
                </div>

                <section className="form-section">
                  <header className="form-section__head">
                    <h4 className="form-section__title">Reason</h4>
                  </header>
                  <p style={{ margin: 0 }}>{detail.reason_summary}</p>
                </section>

                {detail.narrative && (
                  <section className="form-section">
                    <header className="form-section__head">
                      <h4 className="form-section__title">Officer narrative</h4>
                    </header>
                    <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{detail.narrative}</p>
                  </section>
                )}

                {detail.content?.subject && (
                  <section className="form-section">
                    <header className="form-section__head">
                      <h4 className="form-section__title">Subject ({detail.content.subject.kind})</h4>
                    </header>
                    <dl className="str-kv">
                      {Object.entries(detail.content.subject).filter(([k]) => k !== 'kind' && k !== 'directors' && k !== 'ubos').map(([k, v]) => (
                        <React.Fragment key={k}>
                          <dt>{k.replace(/_/g, ' ')}</dt>
                          <dd>{v == null || v === '' ? '—' : String(v)}</dd>
                        </React.Fragment>
                      ))}
                    </dl>
                  </section>
                )}

                {detail.content?.transactions?.rows?.length > 0 && (
                  <section className="form-section">
                    <header className="form-section__head">
                      <h4 className="form-section__title">Transactions ({detail.content.transactions.count}, total {money(detail.content.transactions.total_amount)})</h4>
                    </header>
                    <div style={{ maxHeight: '240px', overflow: 'auto' }}>
                      <table className="tx-table">
                        <thead>
                          <tr>
                            <th>Date</th><th>Direction</th><th>Amount</th>
                            <th>Channel</th><th>Counterparty</th><th>Country</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.content.transactions.rows.map(t => (
                            <tr key={t.id}>
                              <td>{fmt(t.occurred_at)}</td>
                              <td>{t.direction}</td>
                              <td>{money(t.amount, t.currency)}</td>
                              <td>{t.channel || '—'}</td>
                              <td>{t.counterparty_name || '—'}</td>
                              <td>{t.counterparty_country || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                {(detail.content?.alerts?.screening_hits?.length > 0 ||
                  detail.content?.alerts?.tm_alerts?.length > 0) && (
                  <section className="form-section">
                    <header className="form-section__head">
                      <h4 className="form-section__title">Alerts</h4>
                    </header>
                    <p style={{ margin: 0, fontSize: '0.85rem' }}>
                      Screening hits: {detail.content.alerts.screening_hits?.length || 0}
                      &nbsp;&middot;&nbsp;
                      TM alerts: {detail.content.alerts.tm_alerts?.length || 0}
                    </p>
                  </section>
                )}

                <section className="form-section">
                  <header className="form-section__head">
                    <h4 className="form-section__title">Lifecycle</h4>
                  </header>
                  <dl className="str-kv">
                    <dt>Generated</dt><dd>{fmt(detail.generated_at)} by {detail.generated_by_email || '—'}</dd>
                    {detail.reviewed_at  && (<><dt>Reviewed</dt><dd>{fmt(detail.reviewed_at)} by {detail.reviewed_by_email || '—'}</dd></>)}
                    {detail.submitted_at && (<><dt>Submitted</dt><dd>{fmt(detail.submitted_at)} to {detail.submitted_to || '—'} by {detail.submitted_by_email || '—'}</dd></>)}
                    {detail.external_reference && (<><dt>Regulator ref</dt><dd>{detail.external_reference}</dd></>)}
                    {detail.acknowledged_at && (<><dt>Acknowledged</dt><dd>{fmt(detail.acknowledged_at)}</dd></>)}
                    {detail.withdrawn_at  && (<><dt>Withdrawn</dt><dd>{fmt(detail.withdrawn_at)} by {detail.withdrawn_by_email || '—'} — {detail.withdrawal_reason}</dd></>)}
                  </dl>
                </section>

                {/* Lifecycle actions */}
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {canUpdate && (detail.status === 'draft' || detail.status === 'reviewed') && (
                    <button className="btn-cancel" onClick={saveEdits}>Edit</button>
                  )}
                  {canReview && detail.status === 'draft' && (
                    <button className="export-button" onClick={markReviewed}><FaCheck /> Mark reviewed</button>
                  )}
                  {canSubmit && detail.status === 'reviewed' && (
                    <button className="export-button" onClick={submitToRegulator}><FaPaperPlane /> Submit to regulator</button>
                  )}
                  {canSubmit && detail.status === 'submitted' && (
                    <button className="export-button" onClick={acknowledge}><FaCheck /> Mark acknowledged</button>
                  )}
                  {canWithdraw && (detail.status === 'draft' || detail.status === 'reviewed') && (
                    <button className="btn-cancel" onClick={withdraw}><FaUndo /> Withdraw</button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Action modal */}
      {actionModal && (
        <div className="modal-overlay" onClick={() => !actionBusy && setActionModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <button className="modal-close" onClick={() => !actionBusy && setActionModal(null)}>×</button>
            <h3 style={{ marginTop: 0 }}>{actionModal.title}</h3>
            {(actionModal.fields || []).map(f => (
              <div key={f.key} className="form-group" style={{ marginBottom: '0.75rem' }}>
                <label>{f.label}</label>
                {f.type === 'textarea' ? (
                  <textarea rows={4}
                            value={actionVals[f.key] || ''}
                            onChange={e => setActionVals(v => ({ ...v, [f.key]: e.target.value }))} />
                ) : (
                  <input type="text"
                         value={actionVals[f.key] || ''}
                         onChange={e => setActionVals(v => ({ ...v, [f.key]: e.target.value }))} />
                )}
              </div>
            ))}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn-cancel" disabled={actionBusy} onClick={() => setActionModal(null)}>Cancel</button>
              <button className="export-button" disabled={actionBusy} onClick={runAction}>
                {actionBusy ? 'Working…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageSTRReports;
